// Markt-Dashboard in Merxo, Abschnitt 3a (docs/integration/bestell-app.md §20, Stufe 3 der Verschmelzung):
//   S1  Telefonbestellung mit Lieferregeln – `sales.checkout.quote` (Vorschlag) und `sales.order.applyDelivery` (am Entwurf)
//   S2  Abholungen mit Code – `sales.pickup.list | lookup | handover` (Recht `sales.pickup.handover` für Prüfen und Ausgeben)
//   S3  Marktdaten – `sales.store.get | save` (Öffnungszeiten und Hinweis je Standort; die App übernimmt sie über checkoutRules)
//   S4  Meldungen der App als Aufgaben – `sales.channel.reportNotice` (öffentlich, idempotent über `noticeId`)
// Anliegen, Abos und Einwilligungen (S5–S8) gehören zu Abschnitt 3b.
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { PICKUP_CODE_LENGTH } from '../lib/pickup'
import { zCheckoutIssue } from './checkout'
import { zDay, zTime } from './common'
import { zChannelOrderSnapshot } from './tour'
import { brand } from '@shared/brand'

const zText = (max: number) => z.string().trim().max(max)
const zCents = (max = 100_000_000) => z.int({ error: 'Bitte einen Betrag in Cent angeben.' }).min(0, { error: 'Der Betrag darf nicht negativ sein.' }).max(max)
const PAYMENT_METHODS = ['cash', 'ec', 'invoice', 'sepa', 'prepaid'] as const

// ---- Abholungen (S2) -----------------------------------------------------------------------------------------------------------

export const PICKUP_STATES = ['unconfirmed', 'ready', 'done'] as const

export const zPickupRow = z.object({
  orderId: zId,
  /** Auftragsnummer – erst nach der Bestätigung */
  number: z.string().nullable(),
  /** Bestellnummer der App („AL-24851“) */
  externalNumber: z.string().nullable(),
  locationId: zId,
  locationCode: z.string(),
  customer: z.object({ name: z.string(), number: z.string().nullable(), phone: z.string().nullable() }),
  deliveryDate: z.string().nullable(),
  timeFrom: z.string().nullable(),
  timeTo: z.string().nullable(),
  /** „10:00–11:00 Uhr“ */
  windowText: z.string().nullable(),
  bucket: z.enum(['overdue', 'today', 'tomorrow', 'later']),
  /** heute und das Fenster ist vorbei */
  windowPassed: z.boolean(),
  status: z.string(),
  statusLabel: z.string(),
  /** unconfirmed = noch nicht bestätigt (nichts reserviert) · ready = bereit zur Ausgabe · done = übergeben */
  state: z.enum(PICKUP_STATES),
  grossCents: z.int(),
  /** an der Theke zu kassieren (0: bezahlt oder auf Rechnung) */
  amountDueCents: z.int(),
  paymentMethod: z.enum(PAYMENT_METHODS),
  paid: z.boolean(),
  /** Es gibt einen Abholcode (der Code selbst steht nicht in der Liste) */
  hasCode: z.boolean(),
  /** Wer den Code vergeben hat: die App oder Merxo */
  source: z.enum(['app', 'merxo']).nullable(),
  channelLabel: z.string().nullable(),
  minAge: z.int(),
  /** Zahl der Positionen */
  lineCount: z.int(),
  pickedUpAt: z.date().nullable(),
  pickedUpBy: z.string().nullable(),
  lines: z.array(z.object({ name: z.string(), unitLabel: z.string(), qty: z.int(), openQty: z.int(), minAge: z.int().nullable(), depositCents: z.int().nullable() })),
  /** „Abgabe nur an Personen ab 16 Jahren …“ */
  ageNotice: z.string().nullable(),
  note: z.string().nullable(),
  /** Die Ausgabe ist möglich; sonst steht der Grund in `blockedReason` */
  canHandOver: z.boolean(),
  blockedReason: z.string().nullable(),
})

export const pickupList = defineContract({
  name: 'sales.pickup.list',
  title: 'Abholungen anzeigen',
  description: 'Aufträge mit Abholung durch den Kunden: überfällig, heute, morgen und später, dazu die heute übergebenen.',
  llm: {
    description:
      'Liefert die Abholungen (Lieferart Abholung) an den Standorten des Aufrufers: offene Aufträge mit Abholtag, Fenster, Kunde, ' +
      'Betrag „zu kassieren“ (nur bei Barzahlung oder Karte an der Theke) und Zustand, eingeteilt in überfällig, heute, morgen und ' +
      'später; dazu die in den letzten 24 Stunden übergebenen. Den Abholcode nennt die Liste nicht. Verwenden für „Was wird heute ' +
      'in Garching abgeholt?“.',
    keywords: ['Abholung', 'abholen', 'Click & Collect', 'Theke', 'Abholcode', 'überfällig'],
  },
  input: z.strictObject({ locationId: zId.optional() }),
  output: z.object({
    today: z.string(),
    rows: z.array(zPickupRow),
  }),
  permission: 'sales.order.read',
  risk: 'read',
  scope: 'any',
})

export const zPickupOrder = zPickupRow

export const pickupLookup = defineContract({
  name: 'sales.pickup.lookup',
  title: 'Abholcode prüfen',
  description: 'Prüft einen Abholcode (getippt oder aus dem QR-Code) am Standort und zeigt den Auftrag zur Ausgabe.',
  llm: {
    description:
      'Prüft eine Eingabe – den sechsstelligen Abholcode oder den Inhalt des QR-Codes „ALTINGER:<Bestellung>:<Code>“ – gegen die offenen ' +
      'Abholungen des Standorts. Gefunden: der Auftrag mit Positionen, Betrag zu kassieren und Mindestalter. Falsche Codes zählen: höchstens ' +
      '10 Fehlversuche je Person und Stunde, danach 15 Minuten gesperrt. Zeigt nur an – übergeben wird mit sales.pickup.handover.',
    keywords: ['Abholcode', 'QR-Code', 'Abholung prüfen', 'Theke'],
  },
  input: z.strictObject({ locationId: zId, input: z.string().trim().min(1, { error: 'Bitte einen Code eingeben oder scannen.' }).max(200) }),
  output: z.object({
    found: z.boolean(),
    /** gesperrt wegen zu vieler Fehlversuche */
    locked: z.boolean(),
    retryAfterSeconds: z.int().nullable(),
    /** deutscher Satz, wenn nichts gefunden wurde */
    message: z.string().nullable(),
    order: zPickupOrder.nullable(),
  }),
  permission: 'sales.pickup.handover',
  // schreibt (Fehlversuche zählen), liefert aber immer ein Ergebnis statt zu scheitern – sonst rollte die Zählung zurück
  risk: 'write',
  scope: (input) => [input.locationId],
  assistant: 'never',
})

export const pickupHandover = defineContract({
  name: 'sales.pickup.handover',
  title: 'Abholung übergeben',
  description: 'Gibt eine Abholung aus: bucht den Lieferschein (Warenausgang, Zustellung „abgeholt“) und, wenn nötig, die Zahlung an der Theke.',
  llm: {
    description:
      'Übergibt die Ware eines Abholauftrags an die Kundschaft. Prüft den Abholcode erneut (`code`), verlangt bei Artikeln mit Mindestalter ' +
      '`ageChecked: true` und bei offenem Betrag eine Zahlung (`payment`: bar oder Karte). Bucht wie die Meldung „abgeholt“ der Bestell-App: ' +
      'kommissionieren (FEFO), Lieferschein, Zustellung, Kassenvermerk. Ohne Code nur mit `withoutCode` (Grund, z. B. „Ausweis gesehen“). ' +
      'Nicht umkehrbar außer durch eine Rücknahme.',
    keywords: ['Abholung übergeben', 'Ware ausgeben', 'Abholcode', 'Theke', 'kassieren'],
  },
  input: z
    .strictObject({
      orderId: zId,
      /** Abholcode oder Inhalt des QR-Codes */
      code: zText(200).optional(),
      /** Der Kunde hat den Code nicht dabei: Grund (steht im Protokoll) */
      withoutCode: zText(300).min(3, { error: 'Bitte nennen Sie den Grund, warum ohne Code ausgegeben wird.' }).optional(),
      recipientName: zText(120).optional(),
      /** Pflicht true, wenn ein Artikel ein Mindestalter hat */
      ageChecked: z.boolean().optional(),
      /** Pflicht, wenn ein Betrag an der Theke offen ist */
      payment: z.strictObject({ method: z.enum(['cash', 'card']), amountCents: zCents(10_000_000) }).optional(),
      note: zText(300).optional(),
    })
    .refine((input) => (input.code?.trim() ?? '') !== '' || input.withoutCode !== undefined, { error: 'Bitte geben Sie den Abholcode ein – oder nennen Sie den Grund, warum ohne Code ausgegeben wird.', path: ['code'] }),
  output: z.object({
    orderId: zId,
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    delivery: z.object({ id: zId, number: z.string().nullable(), status: z.string(), statusLabel: z.string() }).nullable(),
    dueCents: z.int(),
    collectedCents: z.int(),
    overpaidCents: z.int(),
    emptiesCreditCents: z.int(),
    pickedUpAt: z.date(),
    message: z.string(),
  }),
  permission: 'sales.pickup.handover',
  risk: 'financial',
  scope: 'record',
  assistant: 'never',
  effects: { reversible: false },
})

/** Intern: Die Ausgabe bucht in der Transaktion der Aktion mit den Rechten des Systems (Abonnent), weil das Recht „Abholung übergeben“ Lager- und Zustellrechte einschließt. */
export const pickupHandoverRequested = defineEvent({
  name: 'sales.pickup.handoverRequested',
  title: 'Abholung zur Ausgabe vorgemerkt',
  payload: z.object({
    orderId: zId,
    recipientName: z.string().nullable(),
    ageChecked: z.boolean().nullable(),
    payment: z.object({ method: z.enum(['cash', 'card']), amountCents: z.int() }).nullable(),
    note: z.string().nullable(),
    withoutCode: z.string().nullable(),
  }),
})

// ---- Marktdaten (S3) -----------------------------------------------------------------------------------------------------------

const zDayHours = z.strictObject({ open: zTime, close: zTime })
/** Wochentag der App: '0' (Sonntag) bis '6' (Samstag) → Zeiten oder null (geschlossen) */
export const zOpeningHours = z
  .record(z.string(), zDayHours.nullable())
  .refine((hours) => Object.keys(hours).every((key) => /^[0-6]$/.test(key)), { error: 'Die Wochentage heißen 0 (Sonntag) bis 6 (Samstag).' })

export const zAnnouncement = z.strictObject({
  text: z.string().trim().min(1, { error: 'Bitte einen Hinweistext eingeben.' }).max(280, { error: 'Der Hinweis hat höchstens 280 Zeichen.' }),
  kind: z.enum(['info', 'warning']),
  /** sichtbar von … (einschließlich); null = sofort */
  from: zDay.nullable(),
  /** … bis (einschließlich); null = unbefristet */
  until: zDay.nullable(),
})

const zStoreInfo = z.object({
  location: z.object({ id: zId, code: z.string(), name: z.string() }),
  /** Es sind Marktdaten gespeichert; sonst behält die App ihre eigenen Werte */
  configured: z.boolean(),
  /** Für den Standort sind Lieferregeln gespeichert – erst dann nimmt die App seine Marktdaten über den Spiegel */
  mirrored: z.boolean(),
  /** alle sieben Tage, geschlossene als null */
  openingHours: zOpeningHours,
  announcement: zAnnouncement.nullable(),
  updatedAt: z.date().nullable(),
})

export const storeGet = defineContract({
  name: 'sales.store.get',
  title: 'Marktdaten eines Standorts anzeigen',
  description: 'Öffnungszeiten und Hinweis eines Standorts – die Bestell-App übernimmt sie.',
  llm: {
    description: 'Liefert die Öffnungszeiten (Wochentag 0 = Sonntag … 6 = Samstag, je Tag open/close oder null) und den Hinweis (Text bis 280 Zeichen, Art Info oder Warnung, sichtbar von/bis) eines Standorts.',
    keywords: ['Öffnungszeiten', 'Hinweis', 'Marktdaten', 'Bestell-App'],
  },
  input: z.strictObject({ locationId: zId }),
  output: zStoreInfo,
  permission: 'sales.checkout.read',
  risk: 'read',
  scope: (input) => [input.locationId],
})

export const storeSave = defineContract({
  name: 'sales.store.save',
  title: 'Marktdaten eines Standorts speichern',
  description: 'Speichert Öffnungszeiten und Hinweis; die Bestell-App erfährt es (entprellt) und übernimmt sie.',
  llm: {
    description:
      'Setzt Öffnungszeiten (alle sieben Tage; geschlossen = null) und den Hinweis eines Standorts (null = kein Hinweis). Die Bestell-App ' +
      'übernimmt beides für den Abholstandort und sperrt dort die eigene Pflege. Nur nach Freigabe durch den Betrieb.',
    keywords: ['Öffnungszeiten ändern', 'Hinweis setzen', 'Marktdaten'],
  },
  input: z.strictObject({ locationId: zId, openingHours: zOpeningHours, announcement: zAnnouncement.nullable() }),
  output: zStoreInfo,
  permission: 'sales.checkout.write',
  risk: 'write',
  scope: (input) => [input.locationId],
  assistant: 'opt-in',
  effects: { reversible: true },
})

// ---- Telefonbestellung (S1) ----------------------------------------------------------------------------------------------------

const zFulfillment = z.enum(['delivery', 'pickup'])

export const zDeliveryWindow = z.object({
  start: zTime,
  end: zTime,
  capacity: z.int(),
  booked: z.int(),
  free: z.int(),
  state: z.enum(['free', 'almost', 'full', 'over', 'blocked', 'adhoc']),
  /** der Bestellschluss für dieses Fenster ist vorbei */
  pastCutoff: z.boolean(),
})

/** Was am Auftrag steht: Lieferart, Gutschein, Tragservice und die Beträge der Zeilen – plus die Übersteuerungen des Personals. */
const zApplied = z.object({
  fulfillment: zFulfillment,
  couponCode: z.string().nullable(),
  carryService: z.boolean(),
  deliveryFeeCents: z.int(),
  carryCents: z.int(),
  discountCents: z.int(),
  /** Das Personal hat die Gebühr bzw. den Tragservice abweichend von den Regeln gesetzt */
  overrides: z.array(z.object({ what: z.enum(['delivery', 'carry']), setCents: z.int(), ruleCents: z.int() })),
})

export const checkoutQuote = defineContract({
  name: 'sales.checkout.quote',
  title: 'Lieferung eines Telefonauftrags nach den Lieferregeln vorschlagen',
  description: 'Rechnet für einen Auftrag nach den Lieferregeln: Gebiet der Postleitzahl, Liefergebühr, Tragservice, Gutschein, Prüfhinweise und die Fenster des Tages mit Belegung.',
  llm: {
    description:
      'Schlägt für einen Auftrag (Entwurf, auch noch ohne Positionen) vor, was die Lieferregeln des Standorts verlangen: das Liefergebiet zur Postleitzahl, ' +
      'die Liefergebühr (frei ab, „Lieferung frei“), den Tragservice, den Rabatt eines Gutscheincodes und die Prüfhinweise (Fenster voll oder gesperrt, ' +
      'Bestellschluss, Postleitzahl nicht im Gebiet, Mindestbestellwert, Gutschein ungültig). Dazu die Fenster des Tages mit Belegung. Ändert nichts – ' +
      'übernommen wird mit sales.order.applyDelivery. Nichts davon ist Pflicht.',
    keywords: ['Lieferregeln', 'Telefonbestellung', 'Liefergebühr', 'Fenster', 'Gutschein prüfen'],
  },
  input: z.strictObject({
    locationId: zId,
    /** der gespeicherte Entwurf; ohne ihn gibt es noch keinen Warenwert, keinen Kunden und keine Anschrift */
    orderId: zId.nullable().optional(),
    fulfillment: zFulfillment,
    couponCode: zText(40).nullable().optional(),
    carryService: z.boolean().optional(),
    /** Postleitzahl, wenn sie von der Lieferanschrift des Auftrags abweichen soll */
    zip: z.string().trim().regex(/^\d{5}$/, { error: 'Eine Postleitzahl hat fünf Ziffern.' }).nullable().optional(),
    /** Tag, dessen Fenster gezeigt werden; Standard: der Liefertermin des Auftrags */
    day: zDay.nullable().optional(),
  }),
  output: z.object({
    /** Lieferregeln des Mandanten eingerichtet (erst dann gilt alles hier) */
    configured: z.boolean(),
    /** Fenster und Gebiete dieses Standorts eingerichtet */
    locationConfigured: z.boolean(),
    fulfillment: zFulfillment,
    day: z.string().nullable(),
    zip: z.string().nullable(),
    zone: z.object({ id: zId, name: z.string(), color: z.string(), feeCents: z.int(), minOrderCents: z.int(), freeFromCents: z.int().nullable() }).nullable(),
    goodsGrossCents: z.int(),
    freeDelivery: z.boolean(),
    /** Pauschale des Tragservice, wenn der Betrieb ihn anbietet (0 = nicht angeboten) */
    carryFeeCents: z.int(),
    proposed: z.object({ deliveryFeeCents: z.int(), carryCents: z.int(), discountCents: z.int() }),
    coupon: z.object({ code: z.string(), valid: z.boolean(), message: z.string().nullable() }).nullable(),
    issues: z.array(zCheckoutIssue),
    /** Fenster des Tages; leer an Ausnahmetagen und ohne Vorlagen */
    windows: z.array(zDeliveryWindow),
    closed: z.object({ note: z.string().nullable() }).nullable(),
    /** bereits am Auftrag übernommen (null: noch nicht angewandt) */
    applied: zApplied.nullable(),
  }),
  permission: 'sales.order.write',
  risk: 'read',
  scope: (input) => [input.locationId],
})

export const orderApplyDelivery = defineContract({
  name: 'sales.order.applyDelivery',
  title: 'Lieferung nach den Lieferregeln am Auftrag übernehmen',
  description: 'Schreibt Liefergebühr, Tragservice und Gutschein als Zeilen an den Entwurf und legt die Prüfhinweise am Auftrag ab; eingelöst wird der Gutschein beim Bestätigen.',
  llm: {
    description:
      'Übernimmt am Entwurf eines Auftrags die Lieferung nach den Lieferregeln: Gebühr, Tragservice und Gutschein als Zeilen (ersetzt frühere), die Lieferart ' +
      '(Lieferung oder Abholung – bei Abholung entsteht ein Abholcode) und die Prüfhinweise (stehen beim Bestätigen als ' +
      'Warnung da). Den Gutschein löst erst das Bestätigen des Auftrags ein. `deliveryFeeCents` und `carryCents` übersteuern die Regeln; die ' +
      'Übersteuerung wird am Auftrag vermerkt. Herkunft des Auftrags: Telefon.',
    keywords: ['Lieferung übernehmen', 'Telefonbestellung', 'Liefergebühr', 'Tragservice', 'Gutschein einlösen', 'Abholung'],
  },
  input: z.strictObject({
    id: zId,
    version: z.int().optional(),
    fulfillment: zFulfillment,
    couponCode: zText(40).nullable().optional(),
    carryService: z.boolean().optional(),
    /** Übersteuerung: Liefergebühr bzw. Tragservice (brutto, Cent) statt der Regel; null = nach Regel */
    deliveryFeeCents: zCents(1_000_000).nullable().optional(),
    carryCents: zCents(1_000_000).nullable().optional(),
    zip: z.string().trim().regex(/^\d{5}$/, { error: 'Eine Postleitzahl hat fünf Ziffern.' }).nullable().optional(),
  }),
  output: z.object({
    id: zId,
    version: z.int(),
    applied: zApplied,
    issues: z.array(zCheckoutIssue),
    /** Abholcode bei Abholung; sonst null */
    pickupCode: z.string().length(PICKUP_CODE_LENGTH).nullable(),
    message: z.string(),
  }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
})

// ---- Meldungen der Bestell-App als Aufgaben (S4) -----------------------------------------------------------------------------

export const NOTICE_SEVERITIES = ['info', 'warning', 'critical'] as const

export const channelReportNotice = defineContract({
  name: 'sales.channel.reportNotice',
  title: 'Meldung eines Vertriebskanals als Aufgabe anlegen',
  description: 'Legt zu einer Meldung der Bestell-App, die das Personal erledigen muss, genau eine Aufgabe an (idempotent über `noticeId`).',
  llm: {
    description:
      `Schnittstelle der Bestell-App (öffentliche API). Meldet etwas, das das Personal in ${brand.name} erledigen muss: ein Abo, das nicht ausgeführt werden kann, ` +
      `eine endgültig nicht übertragbare Bestellung, eine Zustellung mit Problem ohne Gegenstück in ${brand.name}, einen Fehler der Anbindung (Schlüssel, Webhook). ` +
      'Dieselbe `noticeId` legt keine zweite Aufgabe an (`replayed: true`). Standort der Aufgabe: der des Auftrags, sonst der liefernde Standort des Zugangs.',
    keywords: ['Meldung', 'Aufgabe', 'Bestell-App', 'Schnittstelle', 'Fehler der Anbindung'],
  },
  input: z.strictObject({
    /** stabile Kennung der App (Art, Bezug, Tag) – Schlüssel der Idempotenz */
    noticeId: zText(200).min(1, { error: 'Die Kennung der Meldung fehlt.' }),
    /** subscription_failed · order_rejected · delivery_failed · connection_key · connection_webhook */
    kind: zText(60).min(1, { error: 'Die Art der Meldung fehlt.' }),
    severity: z.enum(NOTICE_SEVERITIES),
    title: zText(200).min(1, { error: 'Der Titel der Meldung fehlt.' }),
    body: zText(2000),
    ref: z
      .strictObject({
        /** Auftrag in Merxo */
        orderId: zId.optional(),
        /** Bestellung der App */
        externalOrderId: zText(80).optional(),
        customerNumber: zText(40).optional(),
      })
      .optional(),
  }),
  output: z.object({ taskId: zId, replayed: z.boolean() }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
  publicApi: true,
  assistant: 'never',
})

// ---- Aufträge aus Merxo für die App (Ergänzung zu §20.3) ----------------------------------------------------------------------------

export const channelOrderSnapshots = defineContract({
  name: 'sales.channel.orderSnapshots',
  title: `Aufträge aus ${brand.name} für verknüpfte Kunden eines Vertriebskanals abrufen`,
  description: `Auftragsabbilder (Abholcode, Lieferart, Positionen) der Aufträge aus ${brand.name} – Telefon, Theke – für die genannten Kundennummern.`,
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert zu den genannten Kundennummern (höchstens 500, nur die verknüpften Kundinnen der App) die ' +
      `Aufträge, die in ${brand.name} entstanden sind (nicht aus der Bestell-App) und deren Liefertermin im Zeitraum liegt (höchstens 14 Tage): offene, dazu ` +
      'storniert, zugestellt oder abgeholt in den letzten 24 Stunden. Jeder Auftrag im Format des Auftragsabbilds der Touren, mit Abholcode und Lieferart. ' +
      'Andere Kundennummern ergeben nichts.',
    keywords: ['Auftragsabbild', 'Abholcode', 'Bestell-App', 'Schnittstelle', 'Telefonbestellung'],
  },
  input: z
    .strictObject({
      customerNumbers: z.array(zText(40).min(1)).min(1, { error: 'Bitte mindestens eine Kundennummer nennen.' }).max(500),
      from: zDay,
      to: zDay,
    })
    .refine((input) => new Set(input.customerNumbers).size === input.customerNumbers.length, { error: 'Die Kundennummern müssen eindeutig sein.', path: ['customerNumbers'] })
    .refine((input) => input.to >= input.from, { error: 'Das Ende liegt vor dem Beginn.', path: ['to'] })
    .refine((input) => (Date.parse(`${input.to}T00:00:00Z`) - Date.parse(`${input.from}T00:00:00Z`)) / 86_400_000 <= 14, { error: 'Zwischen Beginn und Ende liegen höchstens 14 Tage.', path: ['to'] }),
  output: z.object({ orders: z.array(zChannelOrderSnapshot), serverTime: z.date() }),
  permission: 'sales.order.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
  limits: { maxItems: 500, itemCount: (input) => input.customerNumbers.length },
})
